import { useRouter, useSearchParams } from 'next/navigation';
import React from 'react';
import { SORT_ORDER_OPTIONS } from '../_constants';
import type { SortOrderKey } from '../_constants';
import type { ReadonlyURLSearchParams } from 'next/navigation';

const QUERY_KEYS = {
  PAGE_NO: 'page',
  SEARCH_QUERY: 'query',
  SORT_ORDER: 'sort',
} as const;

function resolvePageNo(searchParams: ReadonlyURLSearchParams | null) {
  const value = searchParams?.get(QUERY_KEYS.PAGE_NO) ?? '';

  const parsed = parseInt(value, 10); // 10進数としてパース
  return isNaN(parsed) || parsed < 1 ? 1 : parsed;
}

function resolveSearchQueryString(
  searchParams: ReadonlyURLSearchParams | null
) {
  return searchParams?.get(QUERY_KEYS.SEARCH_QUERY) ?? '';
}

function resolveSortOrder(
  searchParams: ReadonlyURLSearchParams | null
): SortOrderKey {
  const value = searchParams?.get(QUERY_KEYS.SORT_ORDER) ?? '';

  return SORT_ORDER_OPTIONS.find(({ key }) => key === value)?.key ?? 'add_desc';
}

export function useMyLibraryUrlParams() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const currentPageNo = resolvePageNo(searchParams);
  const searchQueryString = resolveSearchQueryString(searchParams);
  const sortOrder = resolveSortOrder(searchParams);

  const updateSearchQueryString = React.useCallback(
    (newValue: string) => {
      const nextParams = new URLSearchParams({
        [QUERY_KEYS.PAGE_NO]: '1', // page=1 にリセット
        [QUERY_KEYS.SORT_ORDER]: sortOrder,
        [QUERY_KEYS.SEARCH_QUERY]: newValue,
      });

      router.push(`/my-library?${nextParams.toString()}`);
    },
    [router, sortOrder]
  );

  const updateSortOrder = React.useCallback(
    (newValue: SortOrderKey) => {
      const nextParams = new URLSearchParams({
        [QUERY_KEYS.PAGE_NO]: '1', // page=1 にリセット
        [QUERY_KEYS.SORT_ORDER]: newValue,
        [QUERY_KEYS.SEARCH_QUERY]: searchQueryString,
      });

      router.push(`/my-library?${nextParams.toString()}`);
    },
    [router, searchQueryString]
  );

  const updatePagination = React.useCallback(
    (type: 'prev' | 'next') => {
      switch (type) {
        case 'prev': {
          const nextParams = new URLSearchParams({
            [QUERY_KEYS.PAGE_NO]: `${currentPageNo - 1}`,
            [QUERY_KEYS.SORT_ORDER]: sortOrder,
            [QUERY_KEYS.SEARCH_QUERY]: searchQueryString,
          });
          router.push(`/my-library?${nextParams.toString()}`);
          return;
        }

        case 'next': {
          const nextParams = new URLSearchParams({
            [QUERY_KEYS.PAGE_NO]: `${currentPageNo + 1}`,
            [QUERY_KEYS.SORT_ORDER]: sortOrder,
            [QUERY_KEYS.SEARCH_QUERY]: searchQueryString,
          });

          router.push(`/my-library?${nextParams.toString()}`);
          return;
        }
      }
    },
    [currentPageNo, router, searchQueryString, sortOrder]
  );

  return {
    currentPageNo,
    searchQueryString,
    sortOrder,
    updateSortOrder,
    updateSearchQueryString,
    updatePagination,
  };
}
